import {gql} from '@apollo/client';

/**
 * Every document here targets the federated admin endpoint so the Company and
 * Investing schemas compose and PublicEquity fields hydrate.
 *
 * Note what is deliberately absent: the parent `instrumentKey` output field
 * (legacy rows stored as STOCK fail enum serialization) and `ticker` on any
 * pick input (derived from `instrumentId`, and rejected when supplied).
 */

const ERROR_FIELDS = `
  ... on Error {
    message
    statusCode
    type
    errorTypeEnum
    retryAfter
  }
`;

const PAGINATION_FIELDS = `
  pagination {
    page
    limit
    pages
    total
    hasPrevPage
    hasNextPage
    previous
    next
  }
`;

export const GET_EXCHANGES = gql`
  query AdminRecommendationExchanges {
    getAllStocksExchange {
      __typename
      ... on StockExchange {
        id
        code
        stockExchangeName
        baseCurrencyCode
        logoImage
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const GET_RECOMMENDATIONS = gql`
  query AdminInstrumentRecommendations(
    $page: Int!
    $limit: Int!
    $fetchUnpublished: Boolean!
  ) {
    getInstrumentRecommendations(
      page: $page
      limit: $limit
      fetchPrevious: false
      fetchUnPublished: $fetchUnpublished
    ) {
      __typename
      ... on ResponseWithInstrumentRecommendations {
        message
        ${PAGINATION_FIELDS}
        data {
          id
          name
          date
          language
          defaultSource
          availableSources {
            name
            imageUrl
            publishedDate
          }
          lastUpdated
          isPublished
          publishedDate
          exchange {
            id
            code
            stockExchangeName
            baseCurrencyCode
            logoImage
          }
          multiLang {
            language
            name
          }
        }
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const CREATE_RECOMMENDATION = gql`
  mutation CreateInstrumentRecommendation(
    $input: InstrumentRecommendationInput!
  ) {
    createInstrumentRecommendation(input: $input) {
      __typename
      ... on ResponseWithInstrumentRecommendation {
        message
        data {
          id
          name
          date
          language
          isPublished
        }
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const UPDATE_RECOMMENDATION = gql`
  mutation UpdateInstrumentRecommendation(
    $id: ID!
    $input: InstrumentRecommendationInput!
  ) {
    updateInstrumentRecommendation(id: $id, input: $input) {
      __typename
      ... on ResponseWithInstrumentRecommendation {
        message
        data {
          id
          name
          date
          language
          isPublished
        }
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const DELETE_RECOMMENDATION = gql`
  mutation DeleteInstrumentRecommendation($id: ID!) {
    deleteInstrumentRecommendation(id: $id) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

/** The canonical PublicEquity picker. `instruments[].id` is the pick identity. */
export const GET_LISTED_INSTRUMENTS = gql`
  query AdminRecommendationInstrumentPicker(
    $exchangeCode: STOCK_EXCHANGE_CODE!
    $searchTerm: String
    $page: Int!
    $limit: Int!
  ) {
    adminGetStockExchangeListedInstruments(
      stockExchangeCode: $exchangeCode
      searchTerm: $searchTerm
      page: $page
      limit: $limit
      sortInput: {field: PERCENTAGECHANGE, order: DESC}
    ) {
      __typename
      ... on StockExchangeInstrumentList {
        page
        limit
        pages
        total
        hasPrevPage
        hasNextPage
        previous
        next
        instruments {
          id
          ticker
          name
          icon
          currency {
            currencyCode
            symbol
          }
          latestQuote {
            lastPrice
            baseCurrencyCode
            asOf
          }
        }
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const GET_PICKS = gql`
  query AdminRecommendationPicks(
    $recommendationId: ID!
    $source: InstrumentPickSource!
    $type: InstrumentPickType!
    $page: Int!
    $limit: Int!
  ) {
    getInstrumentRecommendationPicks(
      recommendationId: $recommendationId
      source: $source
      type: $type
      page: $page
      limit: $limit
    ) {
      __typename
      ... on ResponseWithInstrumentRecommendationPick {
        message
        ${PAGINATION_FIELDS}
        data {
          reports {
            language
            source
            type
            url
          }
          instrumentRecommendation {
            id
            instrumentRecommendationId
            order
            type
            source
            language
            analysis
            priceAsOf
            priceAsOfDate
            projectedPriceBefore
            projectedPriceBeforeDate
            multiLang {
              language
              analysis
            }
            exchange {
              id
              code
              stockExchangeName
              baseCurrencyCode
            }
            instrumentId
            instrument {
              id
              ticker
              name
              icon
              currency {
                currencyCode
                symbol
              }
              latestQuote {
                lastPrice
                baseCurrencyCode
                asOf
              }
            }
            issuer {
              id
              name
              logoImgURL
            }
          }
        }
      }
      ${ERROR_FIELDS}
    }
  }
`;

/**
 * Registers a source and upserts a whole BUY/HOLD/SELL set — manual rows or a
 * CSV URL, never both. This is the only way to register a source or move the
 * parent default; `addInstrumentRecommendationPick` cannot do either.
 */
export const CREATE_PICK_SET = gql`
  mutation CreateInstrumentRecommendationPicks(
    $input: InstrumentRecommendationPickInput!
  ) {
    createInstrumentRecommendationPick(input: $input) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const ADD_PICK = gql`
  mutation AddInstrumentRecommendationPick(
    $input: AddInstrumentRecommendationPickInput!
  ) {
    addInstrumentRecommendationPick(input: $input) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const UPDATE_PICK = gql`
  mutation UpdateInstrumentRecommendationPick(
    $recommendationId: ID!
    $pickId: ID!
    $language: Language!
    $input: instrumentRecommendationAction!
  ) {
    updateSingleInstrumentRecommendationPick(
      recommendationId: $recommendationId
      pickId: $pickId
      language: $language
      input: $input
    ) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const REMOVE_PICK = gql`
  mutation RemoveInstrumentRecommendationPick(
    $input: RemoveInstrumentRecommendationPickInput!
  ) {
    removeInstrumentRecommendationPick(input: $input) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const TRANSLATE_PICKS = gql`
  mutation TranslateInstrumentRecommendationPicks(
    $recommendationId: ID!
    $targetLanguage: Language!
    $source: InstrumentPickSource!
    $type: InstrumentPickType!
  ) {
    translateInstrumentRecommendationPicks(
      recommendationId: $recommendationId
      targetLanguage: $targetLanguage
      sourceName: $source
      type: $type
    ) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const GENERATE_REPORT = gql`
  mutation GenerateInstrumentRecommendationReport(
    $recommendationId: ID!
    $source: InstrumentPickSource!
    $type: InstrumentPickType!
  ) {
    generateInstrumentRecommendationReport(
      recommendationId: $recommendationId
      source: $source
      type: $type
    ) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const PUBLISH_RECOMMENDATION = gql`
  mutation PublishInstrumentRecommendation($id: ID!) {
    publishInstrumentRecommendation(id: $id) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const CONFIRM_CHANGES = gql`
  mutation ConfirmInstrumentRecommendationChanges(
    $input: ConfirmInstrumentRecommendationChangesInput!
  ) {
    confirmInstrumentRecommendationChanges(input: $input) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;

export const DELETE_SOURCE = gql`
  mutation DeleteInstrumentRecommendationSource(
    $recommendationId: ID!
    $source: InstrumentPickSource!
  ) {
    deleteInstrumentRecommendationPickSource(
      id: $recommendationId
      sourceName: $source
    ) {
      __typename
      ... on Response {
        message
      }
      ${ERROR_FIELDS}
    }
  }
`;
